import { useEffect, useRef, useState } from "react";
import { secureFileUrl } from "../lib/api";

/**
 * Iter123k213 · Render di un'immagine protetta dal backend (richiede JWT).
 * Risolve un URL temporaneo via POST /download-token (`?dt=<token>`) così
 * il browser può caricare l'immagine con un <img src>, senza passare il
 * JWT di sessione nell'URL.
 *
 * props:
 *   path: percorso API (es. "/files/{id}") — passato a secureFileUrl.
 *   alt, className, onClick, onError, onLoad, dataTestid: forwardati all'<img>.
 *   placeholderClassName: classi applicate al placeholder (durante loading/errore).
 */
export default function SecureImg({
  path,
  alt = "",
  className = "",
  onClick,
  onError,
  onLoad,
  dataTestid,
  placeholderClassName = "",
}) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  // Evita setState dopo unmount
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    setErr(false);
    setUrl(null);
    if (!path) { setErr(true); return; }
    (async () => {
      try {
        const u = await secureFileUrl(path);
        if (alive.current) setUrl(u);
      } catch (_e) {
        if (alive.current) { setErr(true); if (onError) onError(_e); }
      }
    })();
    return () => { alive.current = false; };
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) {
    return (
      <div data-testid={dataTestid ? `${dataTestid}-error` : undefined}
        className={`${className} ${placeholderClassName} flex items-center justify-center text-[11px] text-slate-500 bg-slate-100`}
        onClick={onClick}>
        anteprima non disponibile
      </div>
    );
  }
  if (!url) {
    return (
      <div data-testid={dataTestid ? `${dataTestid}-loading` : undefined}
        className={`${className} ${placeholderClassName} animate-pulse bg-slate-200`}
        onClick={onClick} />
    );
  }
  return (
    <img
      data-testid={dataTestid}
      src={url}
      alt={alt}
      className={className}
      onClick={onClick}
      onLoad={onLoad}
      onError={(e) => { setErr(true); if (onError) onError(e); }}
    />
  );
}
